import React, { useEffect, useState } from "react";
import { Avatar, Button, Card, Col, Divider, Input, Row, Space, Typography } from "antd";
import axios from "axios";
import fallbackAvatar from "assets/images/fallbackAvatar.png";
import { message } from "@bindeleddet/core/utils";
import { BLgroup, BLgroupMember, Member } from "@bindeleddet/core/types";
import { EditOutlined, CheckOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

interface Props {
  group: BLgroup;
  allActiveMembers: Member[];
}

const SingleGroup: React.FC<Props> = ({ group, allActiveMembers }) => {
  const [groupMembers, setGroupMembers] = useState<BLgroupMember[]>([]);
  const [membersInGroup, setMembersInGroup] = useState<Member[]>([]);
  const [membersNotInGroup, setMembersNotInGroup] = useState<Member[]>([]);
  const [collapsed, setCollapsed] = useState(true);
  const [editingMember, setEditingMember] = useState<number | null>(null);

  useEffect(() => {
    axios.get<BLgroupMember[]>(`/internal/${group.id}/group-members/`).then(response => {
      setGroupMembers(response.data.sort((a, b) => a.created_at.localeCompare(b.created_at)));
    });
  }, [group.id]);

  useEffect(() => {
    if (groupMembers.length === 0) return;
    setMembersInGroup(
      allActiveMembers.filter(member => groupMembers.some(groupMember => groupMember.bl_member === member.id))
    );
    setMembersNotInGroup(
      allActiveMembers
        .filter(member => !groupMembers.some(groupMember => groupMember.bl_member === member.id))
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
    );
  }, [groupMembers, allActiveMembers]);

  const removeMember = (groupMember: BLgroupMember, member: Member) => {
    axios
      .delete(`/internal/${group.id}/group-members/${groupMember.id}/`)
      .then(() => {
        message("success", `${member.full_name} fjernet fra ${group.name}`);
        setGroupMembers(groupMembers.filter(m => m.id !== groupMember.id));
        setMembersInGroup(membersInGroup.filter(m => m.id !== member.id));
        setMembersNotInGroup([...membersNotInGroup, member]);
      })
      .catch(() => {
        message("error", "Kunne ikke fjerne medlem fra gruppe");
      });
  };

  const updateMember = (groupMember: BLgroupMember, position: string) => {
    if (position === "" || position === groupMember.position) return setEditingMember(null);
    const member_name = membersInGroup.find(m => m.id === groupMember.bl_member)?.full_name;
    axios
      .put(`/internal/${group.id}/group-members/${groupMember.id}/`, {
        ...groupMember,
        position,
        updated_at: new Date().toISOString(),
      })
      .then(() => {
        message("success", `${member_name ?? "Medlem"} satt til ${position} i ${group.name}`);
        setGroupMembers(groupMembers.map(m => (m.id === groupMember.id ? { ...m, position } : m)));
        setEditingMember(null);
      })
      .catch(() => {
        message("error", "Kunne ikke oppdatere medlem");
      });
  };

  const addMember = (member: Member) => {
    axios
      .post<BLgroupMember>(`/internal/${group.id}/group-members/`, {
        bl_member: member.id,
        bl_group: group.id,
        start: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .then(response => {
        message("success", `${member.full_name} lagt til i ${group.name}`);
        setGroupMembers([...groupMembers, response.data]);
        setMembersNotInGroup(membersNotInGroup.filter(m => m.id !== member.id));
        setMembersInGroup([...membersInGroup, member]);
      })
      .catch(() => {
        message("error", "Kunne ikke legge til medlem i gruppe");
      });
  };

  return (
    <Card key={group.id} style={{ width: "100%" }}>
      <Row style={{ position: "relative" }}>
        <Col>
          <Row>
            <Col>
              <Title level={5}>{group.name}</Title>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Typography.Text>{group.email}</Typography.Text>
              </Space>
            </Col>
          </Row>
        </Col>
        <Col style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}>
          <Button
            onClick={() => {
              setCollapsed(!collapsed);
              setEditingMember(null);
            }}
          >
            {collapsed ? "Vis" : "Skjul"}
          </Button>
        </Col>
      </Row>
      {!collapsed ? (
        <>
          <Row gutter={[20, 10]} justify="center" style={{ paddingTop: "10px" }}>
            <Divider />
            {groupMembers.map(groupMember => {
              const member = membersInGroup.find(m => m.id === groupMember.bl_member);
              if (!member) return null;
              return (
                <Col key={groupMember.id}>
                  <Space size={1} direction="vertical" style={{ textAlign: "center" }}>
                    <Avatar
                      src={member.profile_picture ?? fallbackAvatar}
                      size={96}
                      style={{ cursor: "pointer", marginBottom: "2px" }}
                      onClick={() => groupMember && removeMember(groupMember, member)}
                    />
                    <Text strong>{member.full_name.split(" ")[0]}</Text>
                    {editingMember === groupMember.id ? (
                      <>
                        <Input
                          id={`positionInput-${groupMember.id}`}
                          defaultValue={groupMember?.position}
                          style={{ width: "96px", fontSize: "12px", textAlign: "center", padding: "0 4px" }}
                          onPressEnter={e => {
                            updateMember(groupMember, e.currentTarget.value);
                          }}
                          autoFocus={editingMember === groupMember.id}
                        />
                        <CheckOutlined
                          onClick={() => {
                            const input = document.getElementById(
                              `positionInput-${groupMember.id}`
                            ) as HTMLInputElement;
                            if (input) {
                              updateMember(groupMember, input.value);
                            }
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <Text italic>{groupMember?.position ?? "MEDLEM"}</Text>
                        <EditOutlined onClick={() => setEditingMember(groupMember.id)} />
                      </>
                    )}
                  </Space>
                </Col>
              );
            })}
            <Divider />
          </Row>
          <Row gutter={[5, 2]}>
            <Col span={24}>
              <Title level={4}>Legg til medlemmer i {group.name}</Title>
            </Col>

            {membersNotInGroup.map(member => {
              return (
                <Col xl={12} lg={24} sm={24} xs={24} key={member.id}>
                  <Space
                    direction="horizontal"
                    style={{ cursor: "pointer" }}
                    title={`Legg til ${member.full_name}`}
                    onClick={() => {
                      addMember(member);
                    }}
                  >
                    <Avatar src={member.profile_picture ?? fallbackAvatar} />
                    <Text>{member.full_name}</Text>
                  </Space>
                </Col>
              );
            })}
          </Row>
        </>
      ) : null}
    </Card>
  );
};

export default SingleGroup;
